<template>
  <RatingViewContainer>
    <h2 class="heading-2">{{ reviewSubtitle }}</h2>
    <h1 class="heading-1">{{ reviewTitle }}</h1>
    <div class="spacer"></div>
    <p>{{ reviewDescription }}</p>
    <template v-if="showCityField()">
      <RatingSelect v-model="ratingValue" :disabled="!isConfigReady" />
    </template>
    <transition
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="ratingValue || disableAllFields || isEditMode"
        class="transition-opacity"
        :class="{ 'pb-24': city && facility && ratingValue && isMobileDevice }"
      >
        <template v-if="showCityField()">
          <div class="spacer"></div>
          <p class="mb-6 font-semibold">{{ $t('ratingForm.city.title') }}</p>
          <AutocompleteField
            v-model="city"
            :label="$t('ratingForm.city.label')"
            :items="cities"
            :loading="citiesLoading"
            :disabled="disableAllFields && !isEditMode"
            :show-change-button="false"
            @search="debounceCitySearch"
            @focused="cityFocusedHandler"
          />
        </template>

        <div
          v-show="city || disableAllFields || isEditMode"
          class="transition-opacity"
        >
          <template v-if="city || disableAllFields || isEditMode">
            <p class="my-6">
              {{
                showCityField()
                  ? $t('ratingForm.store.title')
                  : $t('ratingForm.store.readonlyTitle')
              }}
            </p>

            <AutocompleteField
              v-model="facility"
              :label="
                !facility
                  ? $t('ratingForm.store.label')
                  : facility.name.toUpperCase()
              "
              item-text="address"
              :items="facilities"
              :loading="facilitiesLoading"
              :disabled="disableAllFields || !facilities.length"
              :show-change-button="!showCityField()"
              @search="debounceFacilititySearch"
              @focused="facilityFocusedHandler"
              @edit-value="editValue"
            >
              <template #item="{ item }">
                <FacilityItem :item="item" />
              </template>
            </AutocompleteField>
            <template v-if="!showCityField()">
              <RatingSelect
                v-model="ratingValue"
                class="mt-2"
                :disabled="!isConfigReady"
              />
            </template>
            <div
              v-if="facility && showOptionalFields"
              class="mt-6 p-6 bg-gray-5 rounded-3xl space-y-4"
            >
              <TextField
                v-if="shouldAcquireEmail"
                v-model="optionalEmail"
                class="flex-grow"
                :label="$t('ratingForm.optionalEmail')"
              />
              <TextField
                v-if="shouldAcquirePhoneNumber"
                v-model="optionalPhoneNumber"
                class="flex-grow"
                :label="$t('ratingForm.optionalPhoneNumber')"
              />
              <FieldControl
                :is-error="isValidationError"
                :error-text="$t('validationError')"
              >
                <CheckboxField v-model="privacyPolicy">
                  {{ $t('common.accept') }}
                  <span
                    class="text-core hover:no-underline underline font-bold"
                    @click.stop="openPrivacyPolicyDialog"
                  >
                    {{ $t('common.privacyPolicy') }}
                  </span>
                </CheckboxField>
              </FieldControl>
            </div>
            <div
              v-if="city && facility && ratingValue"
              class="mt-10"
              :class="{
                'fixed bottom-0 left-0 py-4 px-6 w-full bg-white shadow-pinned-bottom-bar':
                  isMobileDevice,
              }"
            >
              <Button
                block
                :label="$t('ratingForm.button')"
                @click.native="sendReview"
              />
            </div>
          </template>
        </div>
      </div>
    </transition>
    <RegulationsDialog
      v-if="showPrivacyPolicyDialog"
      :title="$t('common.privacyPolicy2')"
      :regulations-url="privacyPolicyUrl"
      @close="showPrivacyPolicyDialog = false"
    />
  </RatingViewContainer>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import RatingSelect from '@/components/legacy/RatingSelect.vue';
import AutocompleteField from '@/components/legacy/AutocompleteField.vue';
import CheckboxField from '@/components/legacy/CheckboxField.vue';
import TextField from '@/components/legacy/TextField.vue';
import FieldControl from '@/components/legacy/FieldControl.vue';
import { City } from '@/models/city';
import { CityService } from '@/services/city.service';
import { Facility } from '@/models/facility';
import { FacilityService } from '@/services/facility.service';
import { ReviewsService } from '@/services/reviews.service';
import FacilityItem from '@/components/legacy/FacilityItem.vue';
import Button from '@/components/legacy/Button.vue';
import RatingViewContainer from '@/components/legacy/RatingViewContainer.vue';
import RegulationsDialog from '@/components/legacy/RegulationsDialog.vue';
import debounce from 'lodash/debounce';
import { TIMERS } from '@/const/timers';
import isMobile from '@/utils/isMobile';
import cuid from 'cuid';
import { DEVICE } from '@/models/enums/device.enum';
import { ReviewPayload } from '@/models/review-payload';

@Component({
  components: {
    RatingSelect,
    AutocompleteField,
    FacilityItem,
    Button,
    TextField,
    CheckboxField,
    FieldControl,
    RegulationsDialog,
    RatingViewContainer,
  },
})
export default class RatingView extends Vue {
  private ratingValue: number = 0;

  private disableAllFields: boolean = false;
  private city: City | null = null;
  private cities: City[] = [];
  private citiesLoading: boolean = false;
  private citiesFetchDisabled: boolean = true;
  private isFocused: boolean = false;
  private optionalEmail: string | null = null;
  private optionalPhoneNumber: string | null = null;
  private privacyPolicy: boolean = false;
  private isMobileDevice: boolean = isMobile();
  private isValidationError: boolean = false;
  private showPrivacyPolicyDialog: boolean = false;

  private get isEditMode(): boolean {
    return !!this.$route.query.edit;
  }

  private debounceCitySearch!: any;
  private debounceFacilititySearch!: any;

  private facility: Facility | null = null;
  private facilities: Facility[] = [];
  private facilitiesLoading: boolean = false;

  private get isConfigReady(): boolean {
    return !!this.$store.state.config;
  }

  private get showOptionalFields(): boolean {
    return this.shouldAcquireEmail || this.shouldAcquirePhoneNumber;
  }

  private get reviewTitle(): string {
    return this.$store.getters['reviewTitle'];
  }
  private get privacyPolicyUrl(): string {
    return this.$store.getters['privacyPolicyUrl'];
  }
  private get shouldAcquireEmail(): boolean {
    return this.$store.getters['shouldAcquireEmail'];
  }

  private get shouldAcquirePhoneNumber(): boolean {
    return this.$store.getters['shouldAcquirePhoneNumber'];
  }

  private get reviewSubtitle(): string {
    return this.$store.getters['reviewSubtitle'];
  }

  private get reviewDescription(): string {
    return this.$store.getters['reviewDescription'];
  }

  private get positiveRatingStart(): number {
    return this.$store.getters['positiveRatingStart'];
  }

  private get defaultFacility(): Facility {
    return this.$store.state.defaultFacility;
  }

  private get isConfigAvailable(): boolean {
    return !!this.$store.state.config;
  }

  private get isPositive(): boolean {
    return this.ratingValue >= this.positiveRatingStart;
  }

  private get reviewParams(): ReviewPayload {
    return {
      rating: this.ratingValue,
      medium: isMobile() ? DEVICE.MOBILE : DEVICE.DESKTOP,
      gmb_address: this.facility!.gmbAddressId,
      email: this.optionalEmail || null,
      phone_number: this.optionalPhoneNumber || null,
    };
  }

  private openPrivacyPolicyDialog() {
    this.showPrivacyPolicyDialog = true;
  }

  async created() {
    this.debounceCitySearch = debounce(
      query => this.fetchCities(query),
      TIMERS.SEARCH_DEBOUNCE
    );

    this.debounceFacilititySearch = debounce(
      query => this.fetchFacilities(query),
      TIMERS.SEARCH_DEBOUNCE
    );
  }

  private showCityField(): boolean {
    return !this.$route.params.type;
  }

  private async cityFocusedHandler() {
    if (!this.citiesFetchDisabled) {
      await this.fetchCities();
    }
    this.citiesFetchDisabled = false;
  }

  private async facilityFocusedHandler() {
    await this.fetchFacilities();
  }
  private validateOptionalFields() {
    if (!this.showOptionalFields || this.privacyPolicy) return;
    if (this.optionalPhoneNumber || this.optionalEmail) {
      this.isValidationError = true;
    }
  }

  private async sendReview() {
    this.validateOptionalFields();
    if (this.isValidationError) return;

    const { id } = await ReviewsService.sendReview(this.reviewParams);
    this.$store.dispatch('setReviewId', id);
    this.$store.dispatch('setWriteReviewUrl', this.facility!.writeReviewUrl);

    this.$router.push({
      name: this.isPositive ? 'positive' : 'review',
      params: { slug: this.$route.params.slug },
    });
  }

  private async fetchCities(searchVal: string | undefined = undefined) {
    if (searchVal) {
      this.city = null;
    }

    const { results } = await CityService.getCities(searchVal);
    this.cities = results;
  }

  private async fetchFacilities(searchVal: string | undefined = undefined) {
    if (searchVal) {
      this.facility = null;
    }
    if (this.city?.name) {
      const { results: facility } = await FacilityService.getFacilities({
        city: this.city.name,
        address: searchVal,
      });
      this.facilities = facility;
    } else {
      this.facilities = [];
    }
  }

  private editValue() {
    this.$router.push({ name: 'rating', query: { edit: 'true' } });
  }

  @Watch('facility')
  private onFacilityChange(current: Facility | null) {
    if (!current || !this.isMobileDevice) return;
    this.$nextTick(() => {
      window.scrollTo(0, document.body.offsetHeight);
    });
  }

  @Watch('privacyPolicy')
  private onPrivacyPolicyChange(current: boolean) {
    if (current) this.isValidationError = false;
  }

  @Watch('city')
  private async onCityChange() {
    if (this.disableAllFields) return;
    this.facilitiesLoading = true;
    await this.fetchFacilities();
    this.facility = null;
    this.facilitiesLoading = false;
  }

  @Watch('defaultFacility', { immediate: true })
  private onDefaultFacility(current: Facility | null) {
    if (!current) return;
    this.disableAllFields = true;
    this.facility = this.defaultFacility;
    this.city = { id: cuid(), name: this.defaultFacility.city };
  }

  @Watch('isConfigAvailable')
  private async onConfigAvilableChange(current: boolean) {
    if (!current || this.disableAllFields || !this.showCityField()) return;

    this.citiesLoading = true;
    await this.fetchCities();
    this.citiesLoading = false;
  }

  @Watch('$route.query.edit', { immediate: true })
  private onRouteChange(current: string, previous: string) {
    if (current) {
      this.facility = null;
      this.city = null;
      this.citiesFetchDisabled = false;
      this.disableAllFields = false;
    }
  }
}
</script>
